<template>
	<div class="pronto-editor" :class="{ disabled }" data-private></div>
</template>

<script>
import store, {
	getChat,
	getCurrentChat,
	getMessages,
	getMyUserId,
	getUser,
	getUsers,
	getUsersForBubble,
	sortUsersByFullname,
} from '@/store'

import Quill from 'quill'
import ShortNameEmoji from './shortNameEmoji.quill'
import MentionBlot from './mentionBlot.quill'
import Mentions from './mentions.quill'
import PlainClipboard from './plainClipboard.quill'
import { renderVueComponent } from '@/helperMethods'
import { normalizeString, removeDiacritics, lowerCase } from './utils'
import { storage, escapeHtml } from '@/helpers/utils'
import EveryoneMention from './EveryoneMention'
import MentionSuggestion from './MentionSuggestion'
import debounce from 'debounce'
import { searchUsers } from '@/api/user'
import { getUserById } from '../Meeting/data'
import { reactive } from 'vue'

const DEFAULT_PLACEHOLDER = 'chat_input_text_placeholder_mentions'
export const ASYNC_MENTIONS_REGEXP = /(^|\s)@((\w.+?))(\s|$)/g

const Block = Quill.import('blots/block')
Block.tagName = 'DIV'
Quill.register(Block, true)

Quill.register(
	{
		'modules/emojis': ShortNameEmoji,
		'formats/mention': MentionBlot,
		'modules/mentions': Mentions,
		'modules/clipboard': PlainClipboard,
	},
	true
)

export default {
	name: 'Editor',
	props: {
		mentionUsers: Array,
		modelValue: null,
		placeholder: String,
		disabled: { type: Boolean, default: null },
		noDraft: { type: Boolean, default: null },
		noEnter: { type: Boolean, default: null },
		noClear: { type: Boolean, default: null },
		autoFocus: { type: Boolean, default: null },
		allowNewLine: { type: Boolean, default: false },
		disableEmojis: { type: Boolean, default: null },
		disableMentions: { type: Boolean, default: null },
		disableAutoResize: { type: Boolean, default: null },
		// Override the chat ID used for draft storage (for thread views)
		chatIdOverride: { type: [Number, String], default: null },
		// Override the chat ID used for mention user list (e.g. parent chat ID for threads)
		mentionChatId: { type: Number, default: null },
	},
	computed: {
		// The effective chat ID for draft storage - uses override if set, otherwise global currentChatId
		effectiveChatId() {
			return this.chatIdOverride ?? store.currentChatId
		},
		// The chat ID to source mention users from
		effectiveMentionChatId() {
			return this.mentionChatId || store.currentChatId
		},
	},
	data() {
		const cachedDrafts = storage.get('pronto_messageDrafts') || {}
		const messageDrafts = reactive(this.noDraft ? {} : cachedDrafts)
		return {
			store,
			users: [],
			messageDrafts,
			resolvingMentions: false,
		}
	},
	emits: [
		'update:modelValue',
		'resolvingMentions',
		'enterPressed',
		'quillPasteEvent',
		'input',
		'inputRaw',
		'keypress',
		'suggestionsChange',
		'blur',
	],
	watch: {
		'store.currentChatId': {
			handler(chatId) {
				// Skip this watcher if using chatIdOverride (thread view has its own chat ID)
				if (this.chatIdOverride) return
				this.editor.focus()
				const draft = this.messageDrafts[chatId]
				if (!chatId || !draft) {
					return this.clear()
				}
				this.setEditorDraft(draft)
				this.lastEmptyAsyncMentionQuery = null
			},
			deep: true,
		},

		placeholder(text) {
			if (text) {
				this.editor.root.dataset.placeholder = text
			}
		},

		disabled() {
			if (!this.editor) return
			this.checkDisabled()
		},

		'store.appVisible': {
			handler(is) {
				if (!is || this.noDraft) return
				// if returning to app from another tab or from a meeting, check to see if there's a draft
				const drafts = localStorage.getItem('pronto_messageDrafts')
				this.messageDrafts = JSON.parse(drafts || '{}')
				const draft = this.messageDrafts[this.effectiveChatId]
				this.setEditorDraft(draft)
			},
			deep: true,
		},

		mentionUsers: {
			handler() {
				this.processUsers()
			},
			deep: true,
		},

		resolvingMentions(isResolving) {
			this.$emit('resolvingMentions', isResolving)
		},
	},

	mounted() {
		const placeholder = this.placeholder || this.$t(DEFAULT_PLACEHOLDER)
		this.options = {
			placeholder,
			formats: [],
			modules: {
				keyboard: {
					bindings: {
						handleEnter: {
							key: 'Enter',
							handler: () => {
								if (this.noEnter) return false
								const output = this.formatOutput()
								this.$emit('enterPressed', output)
								if (this.noClear) return this.allowNewLine
								this.clear()
								return false
							},
						},
						tab: {
							key: 'Tab',
							handler: function (range, context) {
								return true
							},
						},
					},
				},
				clipboard: {
					matchVisual: false,
					allowed: {
						tags: [],
						attributes: [],
					},
				},
			},
		}

		if (!this.disableMentions) {
			this.options.formats.push('mention')
			this.options.modules.mentions = {
				getExplicitResults: this.getExplicitMentionResults.bind(this),
				getImplicitResults: this.getImplicitMentionResults.bind(this),
				getSuggestion: this.getSuggestion.bind(this),
				getUsersInText: this.getUsersInText.bind(this),
				onSuggestionsChange: this.onSuggestionsChange.bind(this),
			}
		}
		if (!this.disableEmojis) {
			this.options.modules.emojis = {
				onSuggestionsChange: this.onSuggestionsChange.bind(this),
			}
		}

		this.editor = new Quill(this.$el, this.options)

		this.editor.root.classList.add('no-highlight')
		this.editor.root.tabIndex = 3

		if (this.modelValue) {
			// See: https://github.com/Hitlabs/pronto-web/issues/2772
			this.editor.root.innerHTML = escapeHtml(this.modelValue)
			EventBus.$emit('quillPasteEvent')
		}

		this.editor.root.addEventListener('blur', this.onBlur)

		this.processUsers()

		this.debouncedOnUsersChange = debounce(this.onUsersChange, 25)

		this.$onEventBus({
			'user-update|membership-update': this.debouncedOnUsersChange,
			'focus-editor': this.onFocus,
			'prepend-mention': this.onPrependMention,
			'clear-prepend-mention': this.onClearPrependMention,
		})

		this.editor.on('text-change', this.onTextChange)

		this.editor.once('editor-change', () => {
			this.$nextTick(() => {
				if (!this.noDraft) {
					const draft = this.messageDrafts[this.effectiveChatId]
					this.setEditorDraft(draft)
				}
				this.checkDisabled()
			})
		})

		if (!this.disableAutoResize) {
			this.resizeObserver = new ResizeObserver((entries) => {
				// this is all so the editor animates any size changes gracefully
				const { contentRect } = entries[0]
				const style = window.getComputedStyle(this.$el)
				const padTop = parseFloat(style.paddingTop)
				const padBottom = parseFloat(style.paddingBottom)
				const height = contentRect.height + padTop + padBottom + 'px'
				Object.assign(this.$el.style, { height })
			})
			this.resizeObserver.observe(this.editor.root)
		}

		// cache message drafts
		if (!this.noDraft) {
			this.$watch(
				() => Object.keys(this.messageDrafts).length,
				(count) => {
					if (count > 0) {
						localStorage.setItem(
							'pronto_messageDrafts',
							JSON.stringify(this.messageDrafts)
						)
					} else {
						localStorage.removeItem('pronto_messageDrafts')
					}
				}
			)
		}

		if (this.autoFocus) {
			this.$nextTick(() => this.onFocus())
		}
	},

	created() {
		this.parseableUsers = []
		this.suggestionInstances = []
	},

	beforeUnmount() {
		// Abort in-flight mention/user fetches — their .then() chains capture
		// `this` and keep the Editor alive until the fetch settles.
		this.asyncMentionCntrl?.abort?.()
		this.asyncMentionCntrl = null
		this.asyncUsersCntrl?.abort?.()
		this.asyncUsersCntrl = null

		Object.keys(this.options?.modules || {})
			.map((moduleName) => this.editor?.getModule(moduleName))
			.filter((module) => module && typeof module.destroy === 'function')
			.forEach((module) => module.destroy())
		this.resizeObserver?.disconnect()
		this.editor?.off('text-change', this.onTextChange)
		this.editor?.root?.removeEventListener('blur', this.onBlur)
		if (this.debouncedOnUsersChange) {
			this.debouncedOnUsersChange.clear()
		}
		this.editor = null
		this.options = null
	},

	methods: {
		checkDisabled() {
			this.disabled ? this.editor.disable() : this.editor.enable()
		},

		formatOutput() {
			const input = this.editor
				.getContents()
				.map(({ insert }) => {
					if (insert.mention) {
						return `<@${insert.mention.id}>`
					}
					if (insert.emoji) {
						return insert.emoji.native
					}
					return insert
				})
				.join('')
			this.$emit('update:modelValue', input)
			this.$emit('input', input)
			this.$emit('inputRaw', input)
			this.$emit('keypress')
			return input
		},

		getExplicitMentionResults(query) {
			if (this.disableMentions) return []
			const chat = getChat(this.effectiveMentionChatId)
			if (chat?.async_mentions_enabled) {
				return this.getAsyncExplicitMentions(query)
			}
			return this.getExplicitMentions(query)
		},

		getImplicitMentionResults(query) {
			if (this.disableMentions) return []
			const chat = getChat(this.effectiveMentionChatId)
			if (chat?.async_mentions_enabled) return []
			const test = lowerCase(removeDiacritics(query))
			return this.users.filter(
				(u) => u.nameParts[0].startsWith(test) && u.id !== 'everyone'
			)
		},

		async getAsyncExplicitMentions(query) {
			// abort any request that might already be in flight
			if (this.asyncMentionCntrl) {
				this.asyncMentionCntrl.abort()
			}
			if (query === '@') {
				// use local cache to get all available users who have
				// recently sent messages and display those
				const allMessages = getMessages()
				const userIds = allMessages.map((m) => m.user_id)
				const unique = Array.from(new Set(userIds))
				const users = unique.map(getUserById).sort(sortUsersByFullname)
				return users.slice(0, 100)
			}
			if (query.startsWith(this.lastEmptyAsyncMentionQuery)) {
				// if we already searched for a subset of this query and already found
				// zero results, then we don't need to continue searching
				return []
			}
			this.lastEmptyAsyncMentionQuery = null
			// fire off a new (abortable) request for the new query
			const chat = getChat(this.effectiveMentionChatId)
			this.asyncMentionCntrl = new AbortController()
			const withoutAt = query.replace('@', '')
			const payload = { 'filter[bubble_ids][]': chat.id }
			const config = { signal: this.asyncMentionCntrl.signal }
			const resp = await searchUsers(withoutAt, payload, config)
			if (!resp) return null
			if (resp.error) {
				this.lastEmptyAsyncMentionQuery = null
				return []
			}
			if (!chat.isdm) {
				// if not dm, check to see if query matches @everyone
				const moniker = this.$t('model_mentions_moniker_everyone').substring(1)
				if (moniker.startsWith(withoutAt)) {
					resp.data.push({
						id: 'everyone',
						fullname: moniker,
						nameNormal: moniker,
						nameParts: [moniker],
						me: true,
					})
				}
			}
			if (resp.data.length) {
				return resp.data
			}
			// if the search yielded no results, save this as a known empty query to
			// optimize future searches that include this same initial string
			this.lastEmptyAsyncMentionQuery = query
			return []
		},

		getExplicitMentions(query) {
			if (query === '@') return this.users.slice(0, 100)
			let test = normalizeString(query.replace('@', ''))
			if (!query.startsWith('@')) return []
			return this.users
				.filter(
					(u) =>
						u.nameNormal.startsWith(test) ||
						u.nameParts.some((p) => p.startsWith(test))
				)
				.sort((a, b) => a.nameNormal.indexOf(test) - b.nameNormal.indexOf(test))
		},

		getSuggestion(user, query) {
			let suggestion = null
			if (user.id === 'everyone') {
				suggestion = renderVueComponent(EveryoneMention, {
					isThread: !!getChat(this.effectiveChatId)?.is_thread,
				})
			} else {
				suggestion = renderVueComponent(MentionSuggestion, { user, query })
			}
			this.suggestionInstances.push(suggestion)
			return suggestion.$el
		},

		hasTextChanged(text) {
			const textAsOfNow = this.editor.getText()
			return text !== textAsOfNow
		},

		getUsersInText(text) {
			const chat = getChat(this.effectiveMentionChatId)
			if (chat?.async_mentions_enabled) {
				return this.getAsyncUsersInText(text)
			}
			return this.getSyncUsersInText(text)
		},

		getAsyncUsersInText(text) {
			this.resolvingMentions = true
			const chat = getChat(this.effectiveMentionChatId)
			// get all matches in text using regexp
			const matches = text.match(ASYNC_MENTIONS_REGEXP) || []
			// bail early if text contains no mentions
			if (!matches.length) return []
			const cleaned = Array.from(matches).map((match) =>
				match.trim().replace('@', '')
			)
			const queries = Array.from(new Set(cleaned))
			this.asyncUsersCntrl?.abort()
			// kick off a request for each match
			this.asyncUsersCntrl = new AbortController()
			const payload = { 'filter[bubble_ids][]': chat.id }
			const config = { signal: this.asyncUsersCntrl.signal }
			const promises = queries.map((match) => {
				return searchUsers(match, payload, config)
					.then((resp) => {
						if (!this.editor) return []
						if (resp === null || resp.error) return []
						this.resolvingMentions = false
						// check to see if text has been altered by user
						if (this.hasTextChanged(text)) return []
						// as each request finishes, add the new users to the list
						// of parseableUsers with normalized data
						this.parseableUsers.push(...resp.data.map(this.processUser))
					})
					.catch((error) => {
						console.error(error)
						return []
					})
			})
			return Promise.all(promises).then(() => {
				if (!this.editor) return []
				if (this.hasTextChanged(text)) return []
				return this.getSyncUsersInText(text)
			})
		},

		getSyncUsersInText(text) {
			const normal = removeDiacritics(lowerCase(text))
			return (
				this.parseableUsers
					.slice()
					// start with the longest names first
					.sort((a, b) => b.nameNormal.length - a.nameNormal.length)
					// find all matches for each user
					.reduce((list, user) => {
						const regex = new RegExp(
							`@${user.fullname}|@${user.nameNormal}`,
							'gi'
						)
						let match = regex.exec(normal)
						while (match !== null) {
							// don't match if user has exact same name as another user
							const hasConflictingName = this.users.some(
								(u) => u.nameNormal === user.nameNormal && u.id !== user.id
							)
							if (hasConflictingName) return list
							// check if there's another match at this same index
							const hasLongerMatch = list.some((m) => m.index === match.index)
							if (!hasLongerMatch) {
								// if not a longer match, add this match to the list
								list.push({
									user,
									index: match.index,
									text: match[0],
								})
							}
							// check for another match for this same user
							match = regex.exec(normal)
						}
						return list
					}, [])
			)
		},

		update() {
			this.$emit(
				'input',
				this.editor.getText() ? this.editor.root.innerHTML : ''
			)
		},

		processUsers() {
			this.users = getUsersForBubble(this.effectiveMentionChatId) || []
			if (!this.users.length) return
			this.users = this.users
				// normalize all of the user names for faster matching
				.map(this.processUser)
				// sort alphabetically
				.sort((a, b) => a.nameNormal.localeCompare(b.nameNormal))
			const chat = getChat(this.effectiveMentionChatId)
			// add in @everyone if not a DM
			if (!chat.isdm || chat.isMultiDM) {
				const everyoneMoniker = this.$t(
					'model_mentions_moniker_everyone'
				).substring(1)
				this.users.push({
					id: 'everyone',
					fullname: everyoneMoniker,
					nameNormal: everyoneMoniker,
					nameParts: [everyoneMoniker],
					me: true,
				})
			}
			if (this.mentionUsers?.length) {
				// "parseableUsers" is a list of users whose mention might appear in the text
				// In practice, this is typically the full list of users in the given chat, plus
				// the message.mentions list from an existing message. This accounts for the case
				// where there is an existing mention for a user without a current membership in
				// the given chat
				this.parseableUsers = this.users.concat(
					this.mentionUsers.map(this.processUser)
				)
			} else {
				this.parseableUsers = this.users
			}
		},

		processUser(user) {
			return {
				...user,
				nameNormal: normalizeString(user.fullname),
				nameParts: normalizeString(user.fullname).split(' '),
				me: user.id === store.auth.user.id,
			}
		},

		onSuggestionsChange(showing, type) {
			if (!showing) {
				this.suggestionInstances
					.filter((x) => x && x.$unmount)
					.forEach((x) => x.$unmount())
				this.suggestionInstances = []
			}
			this.$emit('suggestionsChange', showing, type)
		},

		clear() {
			this.editor.setText('')
			this.editor.updateContents([], Quill.sources.USER)
			this.clearMessageDraft()
			this.lastEmptyAsyncMentionQuery = null
		},

		setMessageDraft() {
			if (this.noDraft) return
			// store a draft of the message for this bubble
			if (this.isEmpty()) {
				this.clearMessageDraft()
			} else {
				const contents = this.editor.getContents()
				this.messageDrafts[this.effectiveChatId] = contents
				localStorage.setItem(
					'pronto_messageDrafts',
					JSON.stringify(this.messageDrafts)
				)
			}
		},

		clearMessageDraft() {
			delete this.messageDrafts[this.effectiveChatId]
			localStorage.setItem(
				'pronto_messageDrafts',
				JSON.stringify(this.messageDrafts)
			)
		},

		isEmpty() {
			const contents = this.editor.getContents()
			if (contents.length === 0) return true
			if (contents.length > 1) return false
			return contents.ops[0].insert.trim?.().length === 0 ?? false
		},

		setEditorDraft(draft) {
			if (!draft || this.noDraft) return
			if (this.deltaIsSameAsCurrentContents(draft)) return
			this.editor.setContents(draft)
			this.update()
			this.editor.setSelection(this.editor.getLength(), 0)
		},

		deltaIsSameAsCurrentContents(delta) {
			if (!delta) return
			const currentDelta = this.editor.getContents()
			return JSON.stringify(currentDelta) === JSON.stringify(delta)
		},

		onFocus() {
			this.editor.setSelection(this.editor.getLength(), 0)
			this.editor.focus()
		},

		onBlur() {
			this.$emit('blur')
			this.previousSelection = this.editor.getSelection()
		},

		onUsersChange() {
			this.processUsers()
		},

		onTextChange(delta, oldDelta) {
			if (oldDelta === '\n') {
				let { insert, attributes } = delta.ops[0]
				newDelta = [{ insert, attributes }]

				if (/^\n+/.test(insert)) {
					let cleanText = insert.replace(/\n+$/, '')

					if (cleanText.length > 0) {
						newDelta.push({
							insert: cleanText,
							attributes,
						})

						this.editor.setContents(newDelta)
					}
				}
			}
			this.previousSelection = null

			this.setMessageDraft()

			this.update()
			this.formatOutput()
		},

		onPrependMention(userId) {
			// if in dm, skip mention
			const chat = getCurrentChat()
			if (chat?.isdm && !chat.isMultiDM) return false
			// if there is content in editor, skip mention
			const user = getUser(userId)
			const length = this.editor.getLength()
			if (userId === getMyUserId() || !user || length > 1) return false
			// otherwise, prepend mention
			this.editor.insertEmbed(0, 'mention', user, Quill.sources.USER)
			this.editor.insertText(1, ' ', Quill.sources.USER)
			setTimeout(() => this.editor.setSelection(2), 0)
		},

		onClearPrependMention() {
			// when leaving thread view, we should clear contents if the
			// only text is a mention and a space
			const { ops } = this.editor.getContents()
			const [one, two] = ops
			if (ops.length === 2 && one.insert.mention && two.insert === ' \n') {
				this.clear()
			}
		},

		insertEmoji(emojiString) {
			if (!emojiString) return
			const text = [emojiString, ' '].join('')
			const { index } = this.previousSelection || this.editor.getSelection(true)
			this.editor.insertText(index, text, Quill.sources.USER)
			setTimeout(() => this.editor.setSelection(index + text.length), 0)
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.pronto-editor {
	--max-height: 200px;
	--padding: 16px;

	white-space: pre-wrap;
	width: 100%;
	font-family: Avenir-Roman;
	font-size: 17px;
	color: var(--neutral-800);
	max-height: var(--max-height);
	caret-color: var(--teal-300);
	padding: var(--padding) 0;
	height: 52px;
	position: relative;
	transition: height 0.1s ease-in-out;
	@media screen and (max-width: 800px) {
		width: calc(100% - 110px);
	}
}

:deep(.ql-editor) {
	outline: none;
	caret-color: var(--teal-300);
	max-height: calc(var(--max-height) - calc(var(--padding) * 2));
	overflow-y: auto;
}

:deep(.ql-clipboard) {
	display: none;
}

:deep(.emoji_completions) {
	position: absolute;
	bottom: calc(100% + 1px);
	left: 40px;
	border-top-left-radius: 10px;
	border-top-right-radius: 10px;
	padding: 8px;
	display: none; // will get set to 'flex' by quill
	flex-wrap: wrap;
	background: var(--neutral-0);
	max-width: 700px;
	border: 1px solid var(--neutral-40040);
	border-bottom: 0;
	box-shadow: 0 -3px 7px 0 rgba(0, 0, 0, 0.13);
	@include max-z;

	> button {
		cursor: pointer;
		display: flex;
		column-gap: 6px;
		align-items: center;
		padding: 2px 6px;
		margin: 0 6px 4px 7px;
		border-radius: 8px;
		font: inherit;
		&.emoji-active,
		&:hover {
			background: var(--blue-100);
			border-color: var(--blue-300);
		}
	}
}

:deep(.emoji-suggestion) {
	color: var(--neutral-800);
	background-color: var(--neutral-0);
	border: 1px solid var(--neutral-200);
}

.disabled {
	:deep(.ql-editor.ql-blank::before) {
		font-family: Avenir-Medium;
		font-size: 17px;
		text-align: center;
		color: var(--neutral-400);
		pointer-events: none;
	}
}

:deep(.mention-completions) {
	$highlight: #f3f3f3;
	position: absolute;
	bottom: calc(100% + 1px);
	width: 100%;
	max-width: 700px;
	display: none; // will get set to 'block' by quill
	box-shadow: 0 -3px 7px 0 rgba(0, 0, 0, 0.13);
	background: var(--neutral-0);
	max-height: 208px;
	min-height: 32px;
	overflow-y: auto;
	overflow-x: hidden;
	padding: 8px 8px;
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
	border: 1px solid var(--neutral-40040);
	border-bottom: 0;
	@include max-z;

	.mention-completions-loading {
		@include absolute-cover(1);
		background: rgba(255, 255, 255, 0.6);
	}

	@media screen and (max-width: 800px) {
		left: 0;
	}
}

:deep(.quill-mention) {
	color: #0c9ef4;
	background: rgba(73, 164, 218, 0.1);
	&.mention-me {
		background: rgba(#ffbb00, 0.18);
	}
}

:deep(.ql-editor.ql-blank::before) {
	color: var(--neutral-300);
	content: attr(data-placeholder);
	font-size: 17px;
	pointer-events: none;
	position: absolute;

	@media screen and (max-width: 800px) {
		font-size: 14px !important;
	}
}

:deep(.emoji-name) {
	font-family: Avenir-Roman;
	font-size: 14px;
	color: var(--neutral-800);
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.pronto-editor {
		.mention-completions,
		.emoji_completions {
			background: var(--neutral-0);
			.mention-option {
				&:hover,
				&.mention-active {
					background: var(--neutral-300) !important;
					.online-indicator {
						border-color: var(--neutral-300);
					}
				}
				.online-indicator {
					border-color: var(--neutral-0);
				}
			}
			.emoji-suggestion {
				&:hover,
				&.emoji-active {
					background: var(--blue-200) !important;
					border-color: var(--blue-400) !important;
				}
			}
		}
		.ql-editor.ql-blank::before {
			color: var(--neutral-400);
		}

		.mention-completions {
			.at-everyone {
				background: var(--neutral-0);
				color: var(--neutral-800);
			}
			.mention-completions-spinner {
				background: rgba(0, 0, 0, 0.6);
			}
		}
	}
}
</style>
